import { useMemo } from 'react';

import { cycleBudgetMonth } from '../components/budget/budgetSpendUtils';
import { useBudgetOverviewQuery } from '../features/budgets/queries';
import { useMonthlySummaryQuery } from '../features/income/queries';

/**
 * Args canónicos para overview do ciclo (Início + Despesas + alertas).
 * `includeArchivedWithSpending: true` por defeito — o gasto de categorias
 * arquivadas conta para «ainda podes gastar».
 */
export function buildCycleOverviewQueryArgs(
  activeCycle,
  { includeArchivedWithSpending = true } = {},
) {
  if (!activeCycle?.start || !activeCycle?.end) {
    return {
      year: undefined,
      month: undefined,
      periodStart: undefined,
      periodEnd: undefined,
      includeArchivedWithSpending,
      enabled: false,
    };
  }

  const { year, month } = cycleBudgetMonth(activeCycle);
  return {
    year,
    month,
    periodStart: activeCycle.start,
    periodEnd: activeCycle.end,
    includeArchivedWithSpending,
    enabled: true,
  };
}

/** Params canónicos para monthly-summary do mesmo ciclo. */
export function buildCycleMonthlySummaryParams(activeCycle) {
  if (!activeCycle?.start || !activeCycle?.end) return null;
  return {
    received_on_from: activeCycle.start,
    received_on_to: activeCycle.end,
  };
}

/**
 * Overview + monthly summary do ciclo activo — mesma fonte em Início e Despesas.
 */
export function useCycleFinancialOverview(
  activeCycle,
  { includeArchivedWithSpending = true } = {},
) {
  const overviewArgs = useMemo(
    () => buildCycleOverviewQueryArgs(activeCycle, { includeArchivedWithSpending }),
    [activeCycle, includeArchivedWithSpending],
  );

  const monthlyParams = useMemo(
    () => buildCycleMonthlySummaryParams(activeCycle),
    [activeCycle],
  );

  const overviewQuery = useBudgetOverviewQuery(overviewArgs);
  const monthlySummaryQuery = useMonthlySummaryQuery(monthlyParams, {
    enabled: overviewArgs.enabled,
  });

  return {
    overview: overviewQuery.data,
    monthlySummary: monthlySummaryQuery.data,
    overviewQuery,
    monthlySummaryQuery,
    isLoading: overviewQuery.isLoading || monthlySummaryQuery.isLoading,
    isPending: overviewQuery.isPending || monthlySummaryQuery.isPending,
    isFetching: overviewQuery.isFetching || monthlySummaryQuery.isFetching,
  };
}
